/**
 * BehavioralAssessmentPage — clinician-facing hub for the Behavioral
 * Assessment feature.
 *
 * Tabs:
 *  - Instruments: catalog + launch an administration for a member; a launched
 *    administration opens the patient questionnaire inline, and once submitted
 *    the clinician review panel is available.
 *  - Simulation: adjust target elevations per scale -> POST /simulate ->
 *    render the T-score profile chart (useful for demos / clinician training,
 *    never real patient data).
 *  - Safety queue: open safety events across administrations.
 *
 * All fetches go through the typed api client (authFetch under the hood).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardList,
  Play,
  FlaskConical,
  ShieldAlert,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TScoreProfileChart } from './TScoreProfileChart';
import { AssessmentQuestionnaire } from './AssessmentQuestionnaire';
import { ClinicianReviewPanel } from './ClinicianReviewPanel';
import { SafetyEventsQueue } from './SafetyEventsQueue';
import { ProvisionalNormsDisclaimer } from './CrisisResources';
import { MemberPicker, type PickedMember } from './MemberPicker';
import {
  behavioralAssessmentApi,
  categoryLabel,
  type InstrumentDetail,
  type InstrumentSummary,
  type SimulateResult,
} from './types';
import { useTabbedChrome } from '@/components/weavein';

type View =
  | { kind: 'hub' }
  | { kind: 'questionnaire'; administrationId: string; instrumentCode: string }
  | { kind: 'review'; administrationId: string };

export const BehavioralAssessmentPage: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [view, setView] = useState<View>({ kind: 'hub' });
  const [tab, setTab] = useState<'instruments' | 'simulation' | 'safety'>('instruments');

  // ── Full-screen sub-views ──────────────────────────────────────
  if (view.kind === 'questionnaire') {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => setView({ kind: 'review', administrationId: view.administrationId })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to review
        </Button>
        <AssessmentQuestionnaire
          administrationId={view.administrationId}
          instrumentCode={view.instrumentCode}
          onSubmitted={(id) => setView({ kind: 'review', administrationId: id })}
        />
      </div>
    );
  }

  if (view.kind === 'review') {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => setView({ kind: 'hub' })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to hub
        </Button>
        <ClinicianReviewPanel
          administrationId={view.administrationId}
          // No documented release endpoint yet; log intent so the flow is usable.
          onRelease={async (id) => {
            // eslint-disable-next-line no-console
            console.info('[behavioral-assessment] clinician released results for', id);
          }}
        />
      </div>
    );
  }

  // ── Hub ─────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6">
        {!tabbed && (
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ClipboardList className="h-6 w-6 text-blue-600" aria-hidden="true" />
          Behavioral Assessment
        </h1>
        )}
        <p className="mt-1 text-sm text-muted-foreground">
          Administer, simulate, and review behavioral assessments.
        </p>
      </header>

      <div className="mb-4">
        <ProvisionalNormsDisclaimer />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="instruments">
            <ClipboardList className="mr-2 h-4 w-4" aria-hidden="true" />
            Instruments
          </TabsTrigger>
          <TabsTrigger value="simulation">
            <FlaskConical className="mr-2 h-4 w-4" aria-hidden="true" />
            Simulation
          </TabsTrigger>
          <TabsTrigger value="safety">
            <ShieldAlert className="mr-2 h-4 w-4" aria-hidden="true" />
            Safety queue
          </TabsTrigger>
        </TabsList>

        <TabsContent value="instruments" className="mt-4">
          <InstrumentsTab
            onLaunched={(administrationId, instrumentCode) =>
              setView({ kind: 'questionnaire', administrationId, instrumentCode })
            }
            onOpenReview={(administrationId) =>
              setView({ kind: 'review', administrationId })
            }
          />
        </TabsContent>

        <TabsContent value="simulation" className="mt-4">
          <SimulationTab />
        </TabsContent>

        <TabsContent value="safety" className="mt-4">
          <SafetyEventsQueue
            onOpenAdministration={(administrationId) =>
              setView({ kind: 'review', administrationId })
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================================
// Instruments tab
// ============================================================================

interface InstrumentsTabProps {
  onLaunched: (administrationId: string, instrumentCode: string) => void;
  onOpenReview: (administrationId: string) => void;
}

const InstrumentsTab: React.FC<InstrumentsTabProps> = ({ onLaunched, onOpenReview }) => {
  const [instruments, setInstruments] = useState<InstrumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<PickedMember | null>(null);
  const [launchingCode, setLaunchingCode] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [reviewIdInput, setReviewIdInput] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await behavioralAssessmentApi.listInstruments();
      setInstruments(res.instruments);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load instruments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const launch = useCallback(
    async (code: string) => {
      // A real (non-simulation) administration must be tied to a member.
      if (!selectedMember) {
        setLaunchError('Select a member before launching an assessment.');
        return;
      }
      setLaunchingCode(code);
      setLaunchError(null);
      try {
        const res = await behavioralAssessmentApi.createAdministration({
          instrument_code: code,
          member_id: String(selectedMember.id),
        });
        onLaunched(res.administration_id, code);
      } catch (e) {
        setLaunchError(e instanceof Error ? e.message : 'Failed to launch administration.');
      } finally {
        setLaunchingCode(null);
      }
    },
    [selectedMember, onLaunched],
  );

  return (
    <div className="space-y-4">
      {/* Launch controls */}
      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <MemberPicker
            id="ba-member-picker"
            label="Member (required)"
            value={selectedMember}
            onChange={setSelectedMember}
          />
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Info className="h-3 w-3" aria-hidden="true" />
            Select a member, then launch an instrument below to begin their
            assessment. To try scoring without a member, use the Simulation tab.
          </p>
        </CardContent>
      </Card>

      {launchError && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {launchError}
        </p>
      )}

      {/* Instruments list */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading instruments…
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : instruments.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No instruments available.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {instruments.map((inst) => (
            <Card key={inst.code} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{inst.name}</CardTitle>
                  <div className="flex shrink-0 gap-1">
                    {inst.is_system && <Badge variant="secondary">System</Badge>}
                    {inst.provisional_norms && (
                      <Badge variant="outline">Provisional</Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {inst.code} · v{inst.version} · {inst.total_items} items · {inst.status}
                </p>
              </CardHeader>
              <CardContent className="mt-auto flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">{inst.description}</p>
                <Button
                  onClick={() => void launch(inst.code)}
                  disabled={launchingCode === inst.code || !selectedMember}
                  title={!selectedMember ? 'Select a member first' : undefined}
                >
                  {launchingCode === inst.code ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  Launch administration
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Open an existing administration for review */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open an existing administration</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="ba-review-id">Administration ID</Label>
            <Input
              id="ba-review-id"
              value={reviewIdInput}
              onChange={(e) => setReviewIdInput(e.target.value)}
              placeholder="Paste an administration ID"
              className="mt-1"
            />
          </div>
          <Button
            variant="outline"
            disabled={!reviewIdInput.trim()}
            onClick={() => onOpenReview(reviewIdInput.trim())}
          >
            Open review
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// Simulation tab
// ============================================================================

const SimulationTab: React.FC = () => {
  const [instruments, setInstruments] = useState<InstrumentSummary[]>([]);
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [detail, setDetail] = useState<InstrumentDetail | null>(null);
  const [targets, setTargets] = useState<Record<string, number>>({});
  const [seed, setSeed] = useState<string>('');
  const [result, setResult] = useState<SimulateResult | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the instrument list once.
  useEffect(() => {
    behavioralAssessmentApi
      .listInstruments()
      .then((res) => {
        setInstruments(res.instruments);
        if (res.instruments.length > 0) setSelectedCode(res.instruments[0].code);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load instruments.'));
  }, []);

  // Load scales when the selected instrument changes.
  useEffect(() => {
    if (!selectedCode) return;
    let cancelled = false;
    setLoadingDetail(true);
    setResult(null);
    behavioralAssessmentApi
      .getInstrument(selectedCode)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setTargets({});
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load scales.');
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCode]);

  // Only clinical-family scales are meaningful simulation targets.
  const targetableScales = useMemo(
    () => (detail?.scales ?? []).filter((s) => s.category !== 'validity'),
    [detail],
  );

  const runSimulation = useCallback(async () => {
    if (!selectedCode) return;
    setRunning(true);
    setError(null);
    try {
      const parsedSeed = seed.trim() === '' ? undefined : Number(seed.trim());
      const res = await behavioralAssessmentApi.simulate({
        instrument_code: selectedCode,
        target_elevations: targets,
        seed: Number.isFinite(parsedSeed) ? parsedSeed : undefined,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Simulation failed.');
    } finally {
      setRunning(false);
    }
  }, [selectedCode, targets, seed]);

  // Enrich simulation output rows with scale names/categories for the chart.
  const chartScales = useMemo(() => {
    if (!result) return [];
    const meta = new Map(detail?.scales.map((s) => [s.code, s]) ?? []);
    return result.scales.map((s) => ({
      scale_code: s.scale_code,
      scale_name: meta.get(s.scale_code)?.name ?? s.scale_code,
      category: meta.get(s.scale_code)?.category ?? 'clinical',
      t_score: s.t_score,
      elevated: s.elevated,
    }));
  }, [result, detail]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run a simulation</CardTitle>
          <p className="text-sm text-muted-foreground">
            Generate a synthetic profile by setting target elevation intensities.
            For demonstration and clinician training only — never real patient data.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label>Instrument</Label>
              <Select value={selectedCode} onValueChange={setSelectedCode}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select an instrument" />
                </SelectTrigger>
                <SelectContent>
                  {instruments.map((inst) => (
                    <SelectItem key={inst.code} value={inst.code}>
                      {inst.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40">
              <Label htmlFor="ba-seed">Seed (optional)</Label>
              <Input
                id="ba-seed"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder="e.g. 42"
                inputMode="numeric"
                className="mt-1"
              />
            </div>
          </div>

          {loadingDetail ? (
            <div className="flex items-center gap-2 py-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading scales…
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">Target elevations (0 – 1)</p>
              {targetableScales.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No adjustable scales for this instrument.
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {targetableScales.map((scale) => {
                    const val = targets[scale.code] ?? 0;
                    return (
                      <div key={scale.code}>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span>
                            {scale.name}{' '}
                            <span className="text-xs text-muted-foreground">
                              ({categoryLabel(scale.category)})
                            </span>
                          </span>
                          <span className="tabular-nums text-muted-foreground">
                            {val.toFixed(2)}
                          </span>
                        </div>
                        <Slider
                          value={[val]}
                          min={0}
                          max={1}
                          step={0.05}
                          onValueChange={([v]) =>
                            setTargets((prev) => ({ ...prev, [scale.code]: v }))
                          }
                          aria-label={`${scale.name} target elevation`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <Button onClick={() => void runSimulation()} disabled={running || !selectedCode}>
            {running ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FlaskConical className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Run simulation
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">Simulated profile</CardTitle>
              <Badge variant="outline">Validity: {result.validity_status}</Badge>
              {result.safety_triggered && (
                <Badge variant="destructive">Safety triggered</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <TScoreProfileChart scales={chartScales} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BehavioralAssessmentPage;
